/**
 * Runtime API base URL switching.
 *
 * The SPA can talk to the API two ways:
 *   - 'public' → https://gastv-api.gasweb.info/api (Azure-fronted, works
 *     from anywhere on the public internet)
 *   - 'tailnet' → https://gastv-backend.taile0df79.ts.net/api (only
 *     reachable when the viewing device is on the gasweb.info tailnet,
 *     but the resolver auto-picks home-proxy mode so streams egress
 *     through tk-bakery / NAS instead of Azure)
 *
 * The user's choice persists in localStorage. On first load we probe
 * the tailnet endpoint with a short timeout — if it's reachable we
 * surface a banner asking the user to switch (auto-detect, opt-in).
 * Dismissing the banner snoozes it for 7 days rather than permanently;
 * otherwise users who move between tailnet and public networks get
 * stuck on whichever mode they were in when they first dismissed.
 *
 * Dev override: when `VITE_STREAMFLOW_API_URL` is set at build time
 * (e.g. local dev hitting localhost:3006), it wins over both options.
 */

const ENV_OVERRIDE = import.meta.env.VITE_STREAMFLOW_API_URL as string | undefined;

const PUBLIC_API  = 'https://gastv-api.gasweb.info/api';
const TAILNET_API = 'https://gastv-backend.taile0df79.ts.net/api';

// Exported so other surfaces (Xtream credential view, .m3u download links,
// etc.) can present BOTH endpoints to the user regardless of which mode
// the SPA itself is in. Browsers may be unable to resolve .ts.net (mobile
// DoH) but native IPTV apps with Tailscale at the OS level can.
export const PUBLIC_API_URL = PUBLIC_API;
export const TAILNET_API_URL = TAILNET_API;

const STORAGE_KEY    = 'gastv.apiBase';
const SNOOZE_KEY     = 'gastv.apiBase.banner-snooze-until';
const SWITCHED_AT_KEY = 'gastv.apiBase.tailnet-switched-at';
const REVERT_REASON_KEY = 'gastv.apiBase.last-revert-reason';

// Window during which consecutive tailnet network failures auto-revert
// the SPA back to public mode. Mobile users on flaky tailnet (DoH DNS
// hiding .ts.net, Tailscale paused mid-session, etc.) end up here.
export const TAILNET_GRACE_WINDOW_MS = 10 * 1000;

// Banner reappears after this many days so users who change networks
// aren't permanently stuck on the wrong API base.
const SNOOZE_DAYS = 7;
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000;

export type ApiBaseMode = 'public' | 'tailnet';

// One-time URL override so mobile users (who can't easily open DevTools
// to flip localStorage) can switch modes by visiting a bookmarked URL.
//   `?api=tailnet` or `?force-tailnet=1` → switch to tailnet, persist,
//                                         then strip the query and reload.
//   `?api=public` → switch back to public.
// Runs synchronously on module import so the very first getApiBaseUrl()
// call sees the new mode.
function consumeUrlOverride(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const api = params.get('api');
    const forceTailnet = params.get('force-tailnet');
    let next: ApiBaseMode | null = null;
    if (api === 'tailnet' || forceTailnet === '1') next = 'tailnet';
    else if (api === 'public') next = 'public';
    if (!next) return;
    localStorage.setItem(STORAGE_KEY, next);
    params.delete('api');
    params.delete('force-tailnet');
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    window.location.replace(newUrl);
  } catch {
    // localStorage unavailable (private mode etc.) — silent no-op.
  }
}
consumeUrlOverride();

export function getApiBaseMode(): ApiBaseMode {
  const stored = localStorage.getItem(STORAGE_KEY) as ApiBaseMode | null;
  return stored === 'tailnet' ? 'tailnet' : 'public';
}

export function getApiBaseUrl(): string {
  if (ENV_OVERRIDE) return ENV_OVERRIDE;
  return getApiBaseMode() === 'tailnet' ? TAILNET_API : PUBLIC_API;
}

/** Set the mode + reload so every in-flight call gets the new base.
 * Reload is acceptable because this is a settings-tier action that
 * happens at most once per device; the UX cost is small.
 *
 * When switching TO tailnet, stamp the switch time so the request layer
 * can auto-revert if the first few calls fail (mobile DNS bypass, etc.). */
export function setApiBaseMode(mode: ApiBaseMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
  if (mode === 'tailnet') {
    localStorage.setItem(SWITCHED_AT_KEY, String(Date.now()));
    localStorage.removeItem(REVERT_REASON_KEY);
  } else {
    localStorage.removeItem(SWITCHED_AT_KEY);
  }
  window.location.reload();
}

/** True if the user switched to tailnet within the grace window. The
 * request layer uses this to decide whether a network failure should
 * auto-revert (during grace) or just bubble up (after grace; user has
 * accepted the mode is sticky). */
export function isWithinTailnetGraceWindow(): boolean {
  if (getApiBaseMode() !== 'tailnet') return false;
  const raw = localStorage.getItem(SWITCHED_AT_KEY);
  if (!raw) return false;
  const switchedAt = parseInt(raw, 10);
  if (isNaN(switchedAt)) return false;
  return Date.now() - switchedAt < TAILNET_GRACE_WINDOW_MS;
}

/** Revert to public mode without a reload. Used by the request layer's
 * auto-flip-back path so an in-flight call can simply retry against
 * the public base on the same tick rather than losing the user's
 * session to a full page reload mid-stream. The caller is expected to
 * retry against the new base. The reason is stored so the UI can
 * surface a one-time toast ("Tailnet unreachable, switched to public"). */
export function revertToPublicSilently(reason: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'public');
    localStorage.removeItem(SWITCHED_AT_KEY);
    localStorage.setItem(REVERT_REASON_KEY, reason);
  } catch {
    /* private mode etc. */
  }
}

/** One-shot read of the last auto-revert reason. Clears on read so the
 * toast only fires once. */
export function consumeRevertReason(): string | null {
  try {
    const reason = localStorage.getItem(REVERT_REASON_KEY);
    if (reason) localStorage.removeItem(REVERT_REASON_KEY);
    return reason;
  } catch {
    return null;
  }
}

/** WebRTC-based tailnet detection. Inspects ICE candidates for an IP in
 * Tailscale's CGNAT range (100.64.0.0/10). Bypasses the browser's DNS
 * resolver entirely — critical for mobile, where Chrome's "Use secure
 * DNS" and iOS Private Relay route programmatic fetch DNS through DoH
 * (Cloudflare/Google) that doesn't know about `.ts.net` even when
 * Tailscale is actively connected. The OS-level Tailscale tunnel still
 * assigns the device a 100.x address, and WebRTC ICE gathering exposes
 * it to JS without any DNS lookup.
 *
 * Returns true when at least one ICE candidate has an IPv4 in 100.64-127
 * (CGNAT). False on timeout, error, or no matching candidate. */
async function detectTailnetViaWebRTC(timeoutMs: number = 1500): Promise<boolean> {
  if (typeof window === 'undefined' || typeof RTCPeerConnection === 'undefined') return false;
  return new Promise<boolean>((resolve) => {
    let pc: RTCPeerConnection | null = null;
    let settled = false;
    const finish = (result: boolean) => {
      if (settled) return;
      settled = true;
      try { pc?.close(); } catch { /* noop */ }
      resolve(result);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    try {
      pc = new RTCPeerConnection({ iceServers: [] });
      pc.createDataChannel('');
      pc.onicecandidate = (e) => {
        if (!e.candidate) return;
        const text = e.candidate.candidate || '';
        // Sample: "candidate:1 1 UDP 2122252543 100.115.249.113 64526 typ host"
        const ipMatch = text.match(/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/);
        if (!ipMatch) return;
        const parts = ipMatch[1].split('.').map((n) => parseInt(n, 10));
        // Tailscale CGNAT: 100.64.0.0/10 → first octet 100, second octet 64-127.
        if (parts[0] === 100 && parts[1] >= 64 && parts[1] <= 127) {
          clearTimeout(timer);
          finish(true);
        }
      };
      pc.createOffer()
        .then((offer) => pc!.setLocalDescription(offer))
        .catch(() => finish(false));
    } catch {
      clearTimeout(timer);
      finish(false);
    }
  });
}

/** Reachability probe for the tailnet endpoint. Layered ladder:
 *   1. WebRTC ICE candidate inspection — bypasses browser DNS, catches
 *      mobile cases where DoH (Chrome secure DNS / iOS Private Relay)
 *      hides `.ts.net` from fetch() while Tailscale is fully connected.
 *   2. Fetch probe to `${TAILNET_API}/debug/whoami` — the original
 *      direct-confirmation path. Works on desktop and on mobile when
 *      the browser uses the OS resolver.
 *
 * Either signal returning true is sufficient. Both run concurrently so
 * the function resolves as soon as the first one confirms.
 *
 * Default 5s timeout for the fetch leg. Cold path on mobile commonly
 * needs ~3-4s (Tailscale DNS thaw + TLS handshake against Tailscale's
 * Let's Encrypt cert + CORS preflight serialize). WebRTC typically
 * completes in <500ms. */
export async function probeTailnetReachable(timeoutMs: number = 5000): Promise<boolean> {
  const fetchLeg = (async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const ac = new AbortController();
        const t = setTimeout(() => ac.abort(), timeoutMs);
        const r = await fetch(`${TAILNET_API}/debug/whoami`, {
          signal: ac.signal,
          mode: 'cors',
          cache: 'no-store',
        });
        clearTimeout(t);
        if (r.ok) return true;
      } catch {
        // fall through to retry
      }
    }
    return false;
  })();
  // Race both legs. Promise.any resolves on the first fulfillment that
  // returns true; if both return false we fall through to false.
  try {
    const results = await Promise.all([detectTailnetViaWebRTC(1500), fetchLeg]);
    return results.some(Boolean);
  } catch {
    return false;
  }
}

export function isBannerSnoozed(): boolean {
  const raw = localStorage.getItem(SNOOZE_KEY);
  if (!raw) return false;
  const until = parseInt(raw, 10);
  if (isNaN(until)) return false;
  return Date.now() < until;
}

export function snoozeBanner(): void {
  localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
}

/** WebSocket URL paired to the current API base. Falls back to the
 * VITE_STREAMFLOW_WS_URL env var only when the user is on 'public' mode
 * and no override is in effect (preserves the existing prod wiring on
 * Vercel where VITE_STREAMFLOW_WS_URL points at gastv-api). */
export function getWsUrl(): string {
  const envWs = import.meta.env.VITE_STREAMFLOW_WS_URL as string | undefined;
  if (!ENV_OVERRIDE && envWs && getApiBaseMode() === 'public') return envWs;
  const apiBase = getApiBaseUrl();
  // Convert https://host/api → wss://host/ws/seats (and http→ws).
  const wsScheme = apiBase.startsWith('https:') ? 'wss:' : 'ws:';
  return apiBase.replace(/^https?:/, wsScheme).replace(/\/api$/, '/ws/seats');
}
